/**
 * Bulgarian Belot's deck, deal, ranking, card points, legality and visibility.
 */
import { SUIT_LABEL, SUITS, cardId, rankOf, suitOf } from '../../core/cards.js';
import { currentWinner } from '../../core/ranking.js';
import { anyOf } from '../../core/legality.js';
import { sameTeam } from '../../core/seats.js';
import { emptyTrick } from '../../core/trick.js';
/** Rules §1.1: 7 to Ace with four players, 9 to Ace with two. Canonical order
 *  is fixed forever: the fairness commitment hashes it. */
export function belotDeck(config) {
    const low = config.players === 2 ? 9 : 7;
    const cards = [];
    for (const suit of SUITS)
        for (let r = low; r <= 14; r++)
            cards.push(cardId(r, suit));
    return Object.freeze(cards);
}
/**
 * Rules §2: three each, then two each, from the dealer's right; the auction;
 * then three more each from the same committed permutation (`onContract`).
 * With two players the eight cards left after that are never dealt.
 */
export function belotDealPlan(_config) {
    return { steps: [{ each: 3 }, { each: 2 }], firstSeatOffset: 1 };
}
// Rules §4: trump J 9 A 10 K Q 8 7; plain A 10 K Q J 9 8 7.
const TRUMP_ORDER = [11, 9, 14, 10, 13, 12, 8, 7];
const PLAIN_ORDER = [14, 10, 13, 12, 11, 9, 8, 7];
const TRUMP_POINTS = { 11: 20, 9: 14, 14: 11, 10: 10, 13: 4, 12: 3 };
const PLAIN_POINTS = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2 };
export function isSuitMode(mode) {
    return mode !== null && mode !== 'NT' && mode !== 'AT';
}
/** Is this card ranked in trump order? Every card in All Trumps; the trump
 *  suit in a suit contract; none in No Trumps. */
export function inTrumpOrder(card, mode) {
    return mode === 'AT' || (isSuitMode(mode) && suitOf(card) === mode);
}
/**
 * All Trumps is the case the core's ranking comment anticipates: every suit
 * ranks J 9 A 10 …, but nothing cuts, so `isTrump` is false everywhere and only
 * `strength` changes. During the auction (no contract) cards rank plain.
 */
export function belotRanking(mode) {
    const trump = isSuitMode(mode) ? mode : null;
    return {
        trumpSuit: trump,
        effectiveSuit: (card) => suitOf(card),
        isTrump: (card) => trump !== null && suitOf(card) === trump,
        strength: (card) => {
            const order = inTrumpOrder(card, mode) ? TRUMP_ORDER : PLAIN_ORDER;
            return order.length - order.indexOf(rankOf(card) ?? 0);
        },
    };
}
/** Card points as they lie, before No Trumps doubles them: 152 in a suit
 *  contract, 248 in All Trumps, 120 in No Trumps. */
export function belotPoints(card, mode) {
    const table = inTrumpOrder(card, mode) ? TRUMP_POINTS : PLAIN_POINTS;
    return table[rankOf(card) ?? 0] ?? 0;
}
export function modeLabel(mode) {
    if (mode === 'NT')
        return 'No Trumps';
    if (mode === 'AT')
        return 'All Trumps';
    return SUIT_LABEL[mode];
}
/** Short form for the strip: ♠, NT, AT. */
export function modeShort(mode) {
    return mode === 'NT' || mode === 'AT' ? mode : SUIT_LABEL[mode];
}
export function belotCurrentTrick(hand) {
    return hand.tricks[hand.tricks.length - 1] ?? emptyTrick(0, hand.dealer);
}
/**
 * The legal-move rules of §5 for every player after the leader:
 *
 *  - No Trumps: follow suit if you can; otherwise anything.
 *  - All Trumps: follow suit, beating the best card of the suit led if you
 *    can; otherwise anything.
 *  - Suit contract, trump led: follow, overtrumping if you can.
 *  - Suit contract, plain suit led and held: follow (no need to beat).
 *  - Void, partner winning: anything.
 *  - Void, an opponent winning: trump, overtrumping a trump already played if
 *    you can. Unable to overtrump, you need not play a losing trump.
 */
export function belotLegalPlays(holding, seat, trick, mode, table) {
    if (trick.plays.length === 0 || trick.ledSuit === null)
        return anyOf(holding);
    const ctx = belotRanking(mode);
    const led = trick.ledSuit;
    const follow = holding.filter((c) => suitOf(c) === led);
    const beating = (cards, against) => {
        const top = against.length === 0 ? -1 : Math.max(...against.map((c) => ctx.strength(c)));
        return cards.filter((c) => ctx.strength(c) > top);
    };
    if (mode === 'NT')
        return anyOf(follow.length > 0 ? follow : holding);
    if (mode === 'AT') {
        if (follow.length === 0)
            return anyOf(holding);
        const onSuit = trick.plays.map((p) => p.card).filter((c) => suitOf(c) === led);
        const over = beating(follow, onSuit);
        return anyOf(over.length > 0 ? over : follow);
    }
    const trumpsPlayed = trick.plays.map((p) => p.card).filter((c) => ctx.isTrump(c));
    if (led === mode) {
        if (follow.length === 0)
            return anyOf(holding);
        const over = beating(follow, trumpsPlayed);
        return anyOf(over.length > 0 ? over : follow);
    }
    if (follow.length > 0)
        return anyOf(follow);
    const winning = currentWinner(trick, ctx);
    if (winning && winning.seat !== seat && sameTeam(table, winning.seat, seat))
        return anyOf(holding);
    const trumps = holding.filter((c) => ctx.isTrump(c));
    if (trumps.length === 0)
        return anyOf(holding);
    const over = beating(trumps, trumpsPlayed);
    return anyOf(over.length > 0 ? over : holding);
}
function handOver(hand, matchFinished) {
    return matchFinished || hand.phase === 'handComplete' || hand.phase === 'abandoned';
}
/** Your own hand, everyone's at the end. Nothing is turned up and nothing is
 *  shown in play: declarations are announced by kind, without their cards. */
export const belotVisibility = {
    holdings(hand, viewerSeat, matchFinished) {
        const over = handOver(hand, matchFinished);
        return hand.secret.holdings.map((_, seat) => (over || seat === viewerSeat ? 'cards' : 'count'));
    },
    kitty: () => 'hidden',
    discards: () => 'hidden',
};
//# sourceMappingURL=rules.js.map